import {
  ApolloError,
  type ApolloClient,
  type DocumentNode,
} from '@apollo/client';
import {
  CreateSiteLayoutDraftDocument,
  type CreateSiteLayoutDraftMutation,
  type CreateSiteLayoutDraftMutationVariables,
  DiscardSiteLayoutDraftDocument,
  type DiscardSiteLayoutDraftMutation,
  type DiscardSiteLayoutDraftMutationVariables,
  Enforcement as EnforcementValue,
  type EntryFieldsFragment,
  type EntrySideFieldsFragment,
  type EntrySideInput,
  EntrySidePosition as EntrySidePositionValue,
  GetSiteLayoutOverviewDocument,
  type GetSiteLayoutOverviewQuery,
  type GetSiteLayoutOverviewQueryVariables,
  GetSiteLayoutStateDocument,
  type GetSiteLayoutStateQuery,
  type GetSiteLayoutStateQueryVariables,
  GetSiteReaderDevicesDocument,
  type GetSiteReaderDevicesQuery,
  type GetSiteReaderDevicesQueryVariables,
  type LayoutRefusalFieldsFragment,
  LayoutRefusalCode,
  LayoutResultKind,
  LayoutSideKind,
  PublishSiteLayoutDraftDocument,
  type PublishSiteLayoutDraftMutation,
  type PublishSiteLayoutDraftMutationVariables,
  RemoveDraftEntryDocument,
  type RemoveDraftEntryMutation,
  type RemoveDraftEntryMutationVariables,
  RemoveDraftZoneDocument,
  type RemoveDraftZoneMutation,
  type RemoveDraftZoneMutationVariables,
  SaveDraftEntryDocument,
  type SaveDraftEntryMutation,
  type SaveDraftEntryMutationVariables,
  SaveDraftZoneDocument,
  type SaveDraftZoneMutation,
  type SaveDraftZoneMutationVariables,
  SeedEntriesFromGatewaysDocument,
  type SeedEntriesFromGatewaysMutation,
  type SeedEntriesFromGatewaysMutationVariables,
  type SiteLayoutFieldsFragment,
  SiteLayoutRevisionStatus,
} from 'apollo/generated/client-operations';
import type { SiteLayoutClient } from './siteLayoutClient';
import { SiteLayoutRefusedError } from './siteLayoutRefusedError';
import { siteReadersOf } from './siteReaders';
import {
  type Enforcement,
  type Entry,
  type EntrySide,
  type EntrySideKind,
  type EntrySidePosition,
  type GatewayDestination,
  type Layout,
  type MovedReader,
  type SavedEntry,
  type SaveEntryInput,
  type SaveEntrySide,
  type SaveZoneInput,
  type SiteLayoutDraft,
  SiteLayoutError,
  type SiteLayoutRefusal,
  type SiteLayoutRevision,
  type SiteLayoutState,
} from './types';

// The schema's enums arrive as the generated enum types; the designer's
// model spells the same values as string literals.
const kindOf = (kind: LayoutSideKind): EntrySideKind =>
  kind === LayoutSideKind.Zone ? 'ZONE' : 'OUTSIDE';
const enforcementOf = (enforcement: EnforcementValue): Enforcement => {
  if (enforcement === EnforcementValue.Enforced) return 'ENFORCED';
  if (enforcement === EnforcementValue.IdentifyOnly) return 'IDENTIFY_ONLY';
  return 'NOT_TRACKED';
};
const positionOf = (position: EntrySidePositionValue): EntrySidePosition =>
  position === EntrySidePositionValue.A ? 'A' : 'B';
const enforcementInput = (enforcement: Enforcement): EnforcementValue => {
  if (enforcement === 'ENFORCED') return EnforcementValue.Enforced;
  if (enforcement === 'IDENTIFY_ONLY') return EnforcementValue.IdentifyOnly;
  return EnforcementValue.NotTracked;
};

const sideFrom = (side: EntrySideFieldsFragment): EntrySide => ({
  id: side.id,
  position: positionOf(side.position),
  kind: kindOf(side.kind),
  zoneId: side.zone?.id ?? null,
  enforcement: enforcementOf(side.enforcement),
});

const entryFrom = (entry: EntryFieldsFragment): Entry => ({
  id: entry.id,
  name: entry.name,
  sideA: sideFrom(entry.sideA),
  sideB: sideFrom(entry.sideB),
});

/** The designer's flat layout from one revision read whole. */
const layoutFrom = (layout: SiteLayoutFieldsFragment): Layout => ({
  zones: layout.zones.map(({ id, name }) => ({ id, name })),
  entries: layout.entries.map(entryFrom),
  readerAssignments: layout.entries.flatMap((entry) =>
    entry.assignments.map((assignment) => ({
      id: assignment.id,
      readerId: assignment.reader.id,
      readerDeviceId: assignment.reader.readerDeviceId,
      readerName: assignment.reader.name,
      entryId: entry.id,
      admitsIntoSideId: assignment.admitsInto.id,
    })),
  ),
});

type Overview = GetSiteLayoutOverviewQuery['getJobsite']['siteLayoutOverview'];

const NO_OVERVIEW: Overview = { effective: null, draft: null, history: [] };

/** Who published a revision, as the versions list names them. */
export const UNKNOWN_PUBLISHER = 'Unknown user';

/**
 * The site's history as the designer holds it, from the overview read first
 * and the layouts read after it: published revisions oldest first, each with
 * the number and publisher the server gives it, and the draft apart with the
 * token and last request id its layout was read with. The overview is a
 * separate snapshot, read first, so its token can lag the layout shown and
 * a write sent with it would be refused as stale.
 * Revisions are the overview's; a layout read since cannot add one. A draft
 * discarded between the two reads has no layout, and is left out, as if the
 * read had come a moment later.
 */
const stateFrom = (
  overview: Overview,
  rows: GetSiteLayoutStateQuery['getJobsite']['siteLayoutRevisions'],
): SiteLayoutState => {
  const layouts = new Map<string, SiteLayoutFieldsFragment>();
  rows.forEach((row) => {
    if (row.layout) layouts.set(row.id, row.layout);
  });
  const revisions: SiteLayoutRevision[] = [];
  [...overview.history].reverse().forEach((summary) => {
    const layout = layouts.get(summary.revisionId);
    if (summary.status === SiteLayoutRevisionStatus.Draft || !layout) return;
    revisions.push({
      ...layoutFrom(layout),
      id: summary.revisionId,
      number: summary.number,
      publishedAt: summary.publishedAt ?? '',
      publishedBy: summary.publishedBy?.name ?? UNKNOWN_PUBLISHER,
      closedAt: summary.effectiveTo ?? null,
    });
  });
  const draftSummary = overview.draft;
  const draftLayout = draftSummary && layouts.get(draftSummary.revisionId);
  const draft: SiteLayoutDraft | null =
    draftSummary && draftLayout
      ? {
          ...layoutFrom(draftLayout),
          revisionId: draftSummary.revisionId,
          number: draftSummary.number,
          draftToken: draftLayout.draftToken ?? 0,
          lastRequestId: draftLayout.lastRequestId ?? null,
        }
      : null;
  return { draft, revisions };
};

const refusalFrom = ({
  code,
  field,
  message,
  entryIds,
  gatewayIds,
}: LayoutRefusalFieldsFragment): SiteLayoutRefusal => {
  switch (code) {
    case LayoutRefusalCode.StaleDraft:
      return { code: 'STALE_DRAFT' };
    case LayoutRefusalCode.NoDraft:
      return { code: 'NO_DRAFT' };
    case LayoutRefusalCode.RevisionMismatch:
      return { code: 'REVISION_MISMATCH' };
    case LayoutRefusalCode.ZoneInUse:
      return { code: 'ZONE_IN_USE', entryIds: entryIds ?? [] };
    case LayoutRefusalCode.SeedNeedsDestination:
      return {
        code: 'SEED_NEEDS_DESTINATION',
        gatewayIds: gatewayIds ?? [],
      };
    default:
      return {
        code: 'FIELD',
        field: field ?? '',
        message: message ?? '',
        ...(gatewayIds?.length ? { gatewayIds } : {}),
      };
  }
};

/** The draft a write names, as this client last read it, and the write's own request id. */
type DraftRef = {
  siteId: string;
  revisionId: string;
  expectedDraftToken: number;
  requestId: string;
};

/** What a composite command answers: applied with the draft's new token, or refused. */
type LayoutPayload = {
  kind: LayoutResultKind;
  refusals: LayoutRefusalFieldsFragment[];
  draftToken?: number | null;
};

/**
 * A publish or discard the server refused because the draft moved on, or a
 * publish refused until the site's gateways are seeded: the code its error
 * carries.
 */
const draftRefusalOf = (error: unknown): SiteLayoutRefusal | null => {
  if (!(error instanceof ApolloError)) return null;
  const codes = error.graphQLErrors.map(({ extensions }) => extensions?.code);
  if (codes.includes('STALE_DRAFT')) return { code: 'STALE_DRAFT' };
  if (codes.includes('REVISION_MISMATCH')) return { code: 'REVISION_MISMATCH' };
  const seedRequired = error.graphQLErrors.find(
    ({ extensions }) => extensions?.code === 'SEED_REQUIRED',
  );
  if (seedRequired) {
    const { gatewayIds } = seedRequired.extensions;
    return {
      code: 'SEED_REQUIRED',
      gatewayIds: Array.isArray(gatewayIds) ? gatewayIds.map(String) : [],
    };
  }
  return null;
};

/** How long a command may go unanswered before the client stops waiting and treats its answer as lost. */
export const COMMAND_DEADLINE_MS = 30_000;

/** The error of a command the client stopped waiting on at the deadline. */
const commandDeadlineError = (): Error =>
  Object.assign(new Error('The layout command did not answer in time'), {
    name: 'CommandDeadlineError',
  });

/**
 * The command's answer, or a `CommandDeadlineError` once the deadline
 * passes; an answer arriving after that is ignored.
 */
const withinDeadline = <Result>(command: Promise<Result>): Promise<Result> =>
  new Promise<Result>((resolve, reject) => {
    const deadline = setTimeout(
      () => reject(commandDeadlineError()),
      COMMAND_DEADLINE_MS,
    );
    command.then(
      (value) => {
        clearTimeout(deadline);
        resolve(value);
      },
      (error) => {
        clearTimeout(deadline);
        reject(error);
      },
    );
  });

/**
 * A command whose answer never arrived: the request may or may not have
 * reached the server. A GraphQL error is an answer; a network failure,
 * a server error status, an unreadable body or the deadline passing is not.
 */
const isLostResponse = (error: unknown): boolean =>
  (error instanceof Error && error.name === 'CommandDeadlineError') ||
  (error instanceof ApolloError &&
    Boolean(error.networkError) &&
    !error.graphQLErrors.length);

const stalledError = (): SiteLayoutError =>
  new SiteLayoutError(
    'STALLED',
    'A change is still waiting on the server. Refresh the page before making more changes.',
  );

/**
 * A command whose answer was lost and which, the overview read again
 * shows, has not landed, or not yet: it was not confirmed.
 */
export const NOT_SAVED_MESSAGE =
  'Odin could not confirm the change was saved. The layout has been read again: check it and try again.';

/**
 * The readers an entry save took off other entries, as the draft it was
 * sent against placed them: what the server answers, for a save whose
 * answer was lost but which landed against that draft.
 */
const movedInDraft = (
  draft: SiteLayoutDraft,
  input: SaveEntryInput,
): MovedReader[] =>
  [...input.sideA.readerDeviceIds, ...input.sideB.readerDeviceIds].flatMap(
    (readerDeviceId) => {
      const placed = draft.readerAssignments.find(
        (assignment) => assignment.readerDeviceId === readerDeviceId,
      );
      return placed && placed.entryId !== input.entryId
        ? [{ readerDeviceId, fromEntryId: placed.entryId }]
        : [];
    },
  );

const newRequestId = (): string => crypto.randomUUID();

/**
 * The layout designer's client over the `siteLayout` GraphQL surface. Each
 * edit is one composite command against the site's draft as this client last
 * read it: the draft's revision and token, and a request id of its own. A
 * command against a draft someone has changed since is refused with nothing
 * written (`STALE_DRAFT`); the designer reads the state again either way.
 *
 * The draft token is the one the draft's layout was read with, in the same
 * snapshot, so a token never runs ahead of the layout it goes with. The
 * overview, read first, lists the versions and reconciles lost answers.
 *
 * A command whose answer is lost is reconciled before anything else is sent:
 * the overview is read again, and a save or removal landed when the draft's
 * last request id is the one sent (other writes advance the token too, so
 * the token proves nothing); a publish landed when the draft it named is now
 * published; a discard landed when that draft is gone and the effective
 * revision is the one read before. When that read fails as well, the client
 * stalls: it sends nothing more until the page is reloaded.
 *
 * Reader placement names the reader device the designer shows; the commands
 * name the site's reader, which is looked up among the readers open on the
 * site. This client never registers a reader.
 *
 * Nothing here goes through the Apollo cache: zones, entries and entry sides
 * keep their ids across revisions while their names and sides differ per
 * revision, so the cache's normalization by id would merge every revision
 * into one.
 */
export class ApolloSiteLayoutClient implements SiteLayoutClient {
  private stalled = false;

  /** Per site, the state of the latest read begun, once it answers. */
  private readonly states = new Map<string, SiteLayoutState>();

  private readonly readsBegun = new Map<string, number>();

  constructor(
    private readonly apollo: ApolloClient<object>,
    private readonly requestId: () => string = newRequestId,
  ) {
    // The Apollo client and the request id source are injected through the
    // parameter properties.
  }

  async getState(siteId: string): Promise<SiteLayoutState> {
    const read = (this.readsBegun.get(siteId) ?? 0) + 1;
    this.readsBegun.set(siteId, read);
    const overview = await this.readOverview(siteId);
    const { data } = await this.apollo.query<
      GetSiteLayoutStateQuery,
      GetSiteLayoutStateQueryVariables
    >({
      query: GetSiteLayoutStateDocument,
      variables: { siteId },
      fetchPolicy: 'no-cache',
    });
    const state = stateFrom(
      overview,
      data.getJobsite?.siteLayoutRevisions ?? [],
    );
    if (this.readsBegun.get(siteId) === read) this.states.set(siteId, state);
    return state;
  }

  async openDraft(
    siteId: string,
    fromRevisionId?: string,
  ): Promise<SiteLayoutDraft> {
    this.assertNotStalled();
    await withinDeadline(
      this.apollo.mutate<
        CreateSiteLayoutDraftMutation,
        CreateSiteLayoutDraftMutationVariables
      >({
        mutation: CreateSiteLayoutDraftDocument,
        variables: { input: { siteId, basedOnRevisionId: fromRevisionId } },
        fetchPolicy: 'no-cache',
      }),
    );
    const { draft } = await this.getState(siteId);
    if (!draft) throw new SiteLayoutRefusedError([{ code: 'NO_DRAFT' }]);
    return draft;
  }

  async discardDraft(siteId: string): Promise<void> {
    this.assertNotStalled();
    const draft = this.draftOf(siteId);
    const effectiveId = this.effectiveIdOf(siteId);
    try {
      await withinDeadline(
        this.apollo.mutate<
          DiscardSiteLayoutDraftMutation,
          DiscardSiteLayoutDraftMutationVariables
        >({
          mutation: DiscardSiteLayoutDraftDocument,
          variables: {
            input: {
              siteId,
              revisionId: draft.revisionId,
              expectedDraftToken: draft.draftToken,
            },
          },
          fetchPolicy: 'no-cache',
        }),
      );
    } catch (error) {
      await this.settle(
        siteId,
        error,
        (overview) =>
          overview.draft?.revisionId !== draft.revisionId &&
          (overview.effective?.revisionId ?? null) === effectiveId,
      );
    }
  }

  async publishDraft(siteId: string): Promise<void> {
    this.assertNotStalled();
    const draft = this.draftOf(siteId);
    try {
      await withinDeadline(
        this.apollo.mutate<
          PublishSiteLayoutDraftMutation,
          PublishSiteLayoutDraftMutationVariables
        >({
          mutation: PublishSiteLayoutDraftDocument,
          variables: {
            input: {
              siteId,
              revisionId: draft.revisionId,
              expectedDraftToken: draft.draftToken,
              requestId: this.requestId(),
            },
          },
          fetchPolicy: 'no-cache',
        }),
      );
    } catch (error) {
      await this.settle(siteId, error, (overview) =>
        overview.history.some(
          ({ revisionId, status }) =>
            revisionId === draft.revisionId &&
            status !== SiteLayoutRevisionStatus.Draft,
        ),
      );
    }
  }

  async saveZone(siteId: string, input: SaveZoneInput): Promise<void> {
    await this.write<
      SaveDraftZoneMutation,
      SaveDraftZoneMutationVariables['input'],
      SaveDraftZoneMutation['saveDraftZone']
    >(
      siteId,
      SaveDraftZoneDocument,
      (ref) => ({ ...ref, zoneId: input.zoneId, name: input.name }),
      (data) => data.saveDraftZone,
    );
  }

  async removeZone(siteId: string, zoneId: string): Promise<void> {
    await this.write<
      RemoveDraftZoneMutation,
      RemoveDraftZoneMutationVariables['input'],
      RemoveDraftZoneMutation['removeDraftZone']
    >(
      siteId,
      RemoveDraftZoneDocument,
      (ref) => ({ ...ref, zoneId }),
      (data) => data.removeDraftZone,
    );
  }

  async saveEntry(siteId: string, input: SaveEntryInput): Promise<SavedEntry> {
    this.assertNotStalled();
    const draft = this.draftOf(siteId);
    const readerIds = await this.readerIdsOf(siteId, draft, [
      ...input.sideA.readerDeviceIds,
      ...input.sideB.readerDeviceIds,
    ]);
    const sideInput = (side: SaveEntrySide): EntrySideInput => ({
      faces:
        side.kind === 'ZONE'
          ? { kind: LayoutSideKind.Zone, zoneId: side.zoneId }
          : { kind: LayoutSideKind.Outside },
      enforcement: enforcementInput(side.enforcement),
      admittingReaderIds: side.readerDeviceIds.map(
        (readerDeviceId) => readerIds.get(readerDeviceId) as string,
      ),
    });
    const payload = await this.write<
      SaveDraftEntryMutation,
      SaveDraftEntryMutationVariables['input'],
      SaveDraftEntryMutation['saveDraftEntry']
    >(
      siteId,
      SaveDraftEntryDocument,
      (ref) => ({
        ...ref,
        entryId: input.entryId,
        name: input.name,
        sideA: sideInput(input.sideA),
        sideB: sideInput(input.sideB),
      }),
      (data) => data.saveDraftEntry,
    );
    if (!payload) return { movedReaders: movedInDraft(draft, input) };
    const deviceIds = new Map(
      [...readerIds].map(([readerDeviceId, readerId]) => [
        readerId,
        readerDeviceId,
      ]),
    );
    return {
      movedReaders: (payload.movedReaders ?? []).map(
        ({ readerId, fromEntryId }) => ({
          readerDeviceId: deviceIds.get(readerId) ?? readerId,
          fromEntryId,
        }),
      ),
    };
  }

  async removeEntry(siteId: string, entryId: string): Promise<void> {
    await this.write<
      RemoveDraftEntryMutation,
      RemoveDraftEntryMutationVariables['input'],
      RemoveDraftEntryMutation['removeDraftEntry']
    >(
      siteId,
      RemoveDraftEntryDocument,
      (ref) => ({ ...ref, entryId }),
      (data) => data.removeDraftEntry,
    );
  }

  /**
   * Seeds the draft from the site's gateways. It takes no draft token: it
   * opens or resumes the draft itself and is safe to send again, a rerun
   * seeding only gateways still without an entry.
   */
  async seedEntriesFromGateways(
    siteId: string,
    destinations?: GatewayDestination[],
  ): Promise<{ seededEntryIds: string[] }> {
    this.assertNotStalled();
    const { data } = await withinDeadline(
      this.apollo.mutate<
        SeedEntriesFromGatewaysMutation,
        SeedEntriesFromGatewaysMutationVariables
      >({
        mutation: SeedEntriesFromGatewaysDocument,
        variables: { input: { siteId, destinations: destinations ?? null } },
        fetchPolicy: 'no-cache',
      }),
    );
    if (!data) throw new Error('The layout command returned nothing');
    const payload = data.seedEntriesFromGateways;
    if (payload.kind === LayoutResultKind.Refused)
      throw new SiteLayoutRefusedError(payload.refusals.map(refusalFrom));
    return { seededEntryIds: payload.seededEntryIds ?? [] };
  }

  private assertNotStalled(): void {
    if (this.stalled) throw stalledError();
  }

  /** The draft as this client last read it; refused when the site had none. */
  private draftOf(siteId: string): SiteLayoutDraft {
    const draft = this.states.get(siteId)?.draft;
    if (!draft) throw new SiteLayoutRefusedError([{ code: 'NO_DRAFT' }]);
    return draft;
  }

  private effectiveIdOf(siteId: string): string | null {
    const revisions = this.states.get(siteId)?.revisions ?? [];
    return revisions.length ? revisions[revisions.length - 1].id : null;
  }

  /**
   * Sends one composite command against the draft as last read, with a
   * request id of its own. Answers its payload when applied, or nothing when
   * its answer was lost and the overview shows it landed; a refusal throws.
   */
  private async write<
    Result,
    Input extends DraftRef,
    Payload extends LayoutPayload,
  >(
    siteId: string,
    mutation: DocumentNode,
    inputOf: (ref: DraftRef) => Input,
    payloadOf: (data: Result) => Payload,
  ): Promise<Payload | null> {
    this.assertNotStalled();
    const draft = this.draftOf(siteId);
    const requestId = this.requestId();
    const input = inputOf({
      siteId,
      revisionId: draft.revisionId,
      expectedDraftToken: draft.draftToken,
      requestId,
    });
    let data: Result | null | undefined;
    try {
      ({ data } = await withinDeadline(
        this.apollo.mutate<Result, { input: Input }>({
          mutation,
          variables: { input },
          fetchPolicy: 'no-cache',
        }),
      ));
    } catch (error) {
      await this.settle(
        siteId,
        error,
        (overview) => overview.draft?.lastRequestId === requestId,
      );
      return null;
    }
    if (!data) throw new Error('The layout command returned nothing');
    const payload = payloadOf(data);
    if (payload.kind === LayoutResultKind.Refused)
      throw new SiteLayoutRefusedError(payload.refusals.map(refusalFrom));
    // The next write goes against the draft as this one left it, until the
    // state is read again.
    const state = this.states.get(siteId);
    if (state?.draft)
      this.states.set(siteId, {
        ...state,
        draft: {
          ...state.draft,
          draftToken: payload.draftToken ?? state.draft.draftToken,
          lastRequestId: requestId,
        },
      });
    return payload;
  }

  /**
   * After a command failed: a lost answer is reconciled against the overview
   * read afresh, and passes when `landed` says the command went through; a
   * draft that moved on is the refusal it is; anything else throws as it is.
   */
  private async settle(
    siteId: string,
    error: unknown,
    landed: (overview: Overview) => boolean,
  ): Promise<void> {
    const refusal = draftRefusalOf(error);
    if (refusal) throw new SiteLayoutRefusedError([refusal]);
    if (!isLostResponse(error)) throw error;
    let overview: Overview;
    try {
      overview = await this.readOverview(siteId);
    } catch {
      this.stalled = true;
      throw stalledError();
    }
    if (!landed(overview)) throw new Error(NOT_SAVED_MESSAGE);
  }

  /** The overview, within the deadline: a reconciliation that cannot read it stalls the client rather than waiting forever. */
  private async readOverview(siteId: string): Promise<Overview> {
    const { data } = await withinDeadline(
      this.apollo.query<
        GetSiteLayoutOverviewQuery,
        GetSiteLayoutOverviewQueryVariables
      >({
        query: GetSiteLayoutOverviewDocument,
        variables: { siteId },
        fetchPolicy: 'no-cache',
      }),
    );
    return data.getJobsite?.siteLayoutOverview ?? NO_OVERVIEW;
  }

  /**
   * The site's reader for each reader device: those the draft places, then
   * the readers open on the site. A device with none is refused, as the
   * server words it, before anything is sent.
   */
  private async readerIdsOf(
    siteId: string,
    draft: SiteLayoutDraft,
    readerDeviceIds: string[],
  ): Promise<Map<string, string>> {
    const readerIds = new Map(
      draft.readerAssignments.map(({ readerDeviceId, readerId }) => [
        readerDeviceId,
        readerId,
      ]),
    );
    if (readerDeviceIds.some((id) => !readerIds.has(id))) {
      const { data } = await withinDeadline(
        this.apollo.query<
          GetSiteReaderDevicesQuery,
          GetSiteReaderDevicesQueryVariables
        >({
          query: GetSiteReaderDevicesDocument,
          variables: { jobsiteId: siteId },
          fetchPolicy: 'no-cache',
        }),
      );
      siteReadersOf(data).forEach(({ id, readerId }) =>
        readerIds.set(id, readerId),
      );
    }
    if (readerDeviceIds.some((id) => !readerIds.has(id)))
      throw new SiteLayoutRefusedError([
        {
          code: 'FIELD',
          field: 'readers',
          message: 'Reader is not registered on this site',
        },
      ]);
    return readerIds;
  }
}
